'use client'

import { getCurrentUser, signOut } from 'aws-amplify/auth'

import { sanitizeRedirectPath } from '@/services/auth/redirectPath'
import { clearAmplifyBrowserAuthArtifacts } from '@/services/auth/amplifyStorageCleanup.client'
import { isAuthHardFailure } from '@/services/auth/authErrorClassifier'
import { clearLastActivity } from '@/services/auth/inactivity.client'
import { clearRedirectAttempted } from '@/services/auth/publicAuthRedirectMarker.client'

const PERSIST_SESSION_KEY = 'persist-session-v1'
const LEGACY_PERSIST_SESSION_KEY = 'persist-session'

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export const setPersistSessionPreference = (enabled: boolean): void => {
  try {
    const value = String(Boolean(enabled))
    localStorage.setItem(PERSIST_SESSION_KEY, value)
    // Migration compatibility for old key readers during rollout.
    localStorage.setItem(LEGACY_PERSIST_SESSION_KEY, value)
  } catch {}
}

export const getPersistSessionPreference = (): boolean => {
  try {
    const fromV1 = localStorage.getItem(PERSIST_SESSION_KEY)
    if (fromV1 !== null) return fromV1 === 'true'
    const fromLegacy = localStorage.getItem(LEGACY_PERSIST_SESSION_KEY)
    return fromLegacy === 'true'
  } catch {
    return false
  }
}

export const clearPersistSessionPreference = (): void => {
  try {
    localStorage.removeItem(PERSIST_SESSION_KEY)
    localStorage.removeItem(LEGACY_PERSIST_SESSION_KEY)
  } catch {}
}

export function clearRecoverableClientAuthArtifacts(): void {
  clearLastActivity()
  clearRedirectAttempted()
  clearAmplifyBrowserAuthArtifacts()
}

export async function hasAuthenticatedUserSession(): Promise<boolean> {
  try {
    // Amplify v6 getCurrentUser() reads tokens and throws when tokens are unavailable.
    // We intentionally avoid fetchAuthSession() here to prevent credential-provider side effects.
    await getCurrentUser()
    return true
  } catch (error) {
    if (isAuthHardFailure(error)) {
      clearRecoverableClientAuthArtifacts()
    }
    return false
  }
}

type WaitForAuthenticatedUserSessionOptions = {
  timeoutMs?: number
  pollMs?: number
  maxPollMs?: number
  backoffFactor?: number
}

export async function waitForAuthenticatedUserSession(
  options?: WaitForAuthenticatedUserSessionOptions,
): Promise<boolean> {
  const timeoutMs = options?.timeoutMs ?? 2_000
  const initialPollMs = options?.pollMs ?? 80
  const maxPollMs = options?.maxPollMs ?? 400
  const backoffFactor = options?.backoffFactor ?? 1.35
  const startedAt = Date.now()
  let pollMs = Math.max(40, initialPollMs)

  while (Date.now() - startedAt < timeoutMs) {
    if (await hasAuthenticatedUserSession()) {
      return true
    }
    await sleep(pollMs)
    pollMs = Math.min(maxPollMs, Math.ceil(pollMs * backoffFactor))
  }

  return false
}

export async function signOutLocalSessionBestEffort(): Promise<void> {
  try {
    await signOut({ global: false })
  } catch {}
}

export { sanitizeRedirectPath }
